/**
 * The inspector's six tabs, as data.
 *
 * The tab is part of the URL (`/flights/:id/:tab`), so a banner can link to
 * `…/terrain`, the back button works, and a screenshot of the address bar says
 * which view it was. This registry is the one place that knows the order, the
 * label, the icon and which component draws each pane; the panes themselves are
 * imported through their `index.tsx`, which is the only path the shell touches.
 */
import type { ComponentType, ReactNode } from 'react';
import type { Aircraft } from '../api';
import ExportPane from '../panes/export';
import { FormPane } from '../panes/form/FormPane';
import NavLogPane from '../panes/navlog';
import { RoutePane } from '../panes/route/RoutePane';
import TerrainPane from '../panes/terrain';
import WeatherPane from '../panes/weather';
import {
  IconExport, IconForm, IconMap, IconNavLog, IconTerrain, IconWeather,
} from './icons';

export type TabKey = 'route' | 'navlog' | 'weather' | 'terrain' | 'form' | 'export';

/** What a pane may be handed. Everything else it reads from a context. */
export interface PaneContext {
  readOnly: boolean;
  aircraft: Aircraft[];
  aircraftId: string | null;
  onPickAircraft?: ((id: string | null) => void) | undefined;
}

export interface TabDef {
  key: TabKey;
  labelKey: string;
  Icon: ComponentType<{ size?: number }>;
  /** the pane brings its own padding (the form and the route list do) */
  bare?: boolean;
  render(ctx: PaneContext): ReactNode;
}

export const TABS: readonly TabDef[] = [
  {
    key: 'route',
    labelKey: 'insp.route',
    Icon: IconMap,
    bare: true,
    render: ctx => <RoutePane readOnly={ctx.readOnly} />,
  },
  { key: 'navlog', labelKey: 'insp.navlog', Icon: IconNavLog, render: () => <NavLogPane /> },
  { key: 'weather', labelKey: 'insp.weather', Icon: IconWeather, render: () => <WeatherPane /> },
  { key: 'terrain', labelKey: 'insp.terrain', Icon: IconTerrain, render: () => <TerrainPane /> },
  {
    key: 'form',
    labelKey: 'insp.form',
    Icon: IconForm,
    bare: true,
    render: ctx => (
      <FormPane
        aircraft={ctx.aircraft}
        aircraftId={ctx.aircraftId}
        {...(ctx.onPickAircraft ? { onPickAircraft: ctx.onPickAircraft } : {})}
      />
    ),
  },
  { key: 'export', labelKey: 'insp.export', Icon: IconExport, render: () => <ExportPane /> },
];

export const DEFAULT_TAB: TabKey = 'route';

export const isTabKey = (s: string | undefined): s is TabKey =>
  TABS.some(tab => tab.key === s);

export const tabOf = (s: string | undefined): TabKey => (isTabKey(s) ? s : DEFAULT_TAB);
